// src/features/admin/roleMappingApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// Admin token refresh utility
const adminRefreshToken = async () => {
  const email = localStorage.getItem('admin_email');
  const password = localStorage.getItem('admin_password');
  
  if (!email || !password) return null;
  
  try {
    const response = await fetch(`${process.env.REACT_APP_SERVER_ENDPOINT}/admin/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'authkey': '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3',
      },
      body: JSON.stringify({
        email: email.trim(),
        password: password,
      }),
    });
    
    const data = await response.json();
    
    if (response.ok && data.token) {
      localStorage.setItem('admin_token', data.token);
      return data.token;
    }
    
    return null;
  } catch (err) {
    console.error('Admin token refresh failed:', err);
    return null;
  }
};

const BASE_URL = process.env.REACT_APP_SERVER_ENDPOINT;
const AUTH_KEY = '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3';

// Permission structure
export interface Permission {
  menu_id: string;
  menu_name: string;
  can_read: boolean;
  can_write: boolean;
}

// Role mapping structure
export interface RoleMapping {
  id: number;
  role_id: number;
  role_name: string;
  admin_type: 'super_admin' | 'admin' | 'operation';
  module_or_screen: string | Record<string, string>;
  rights: string;
  createddt?: string;
  updateddt?: string | null;
  created_at?: string;
  updated_at?: string | null;
  recid?: number;
}

// Request/Response types
export interface CreateRoleMappingRequest {
  role_id: number;
  admin_type?: 'super_admin' | 'admin' | 'operation';
  module_or_screen: Record<string, string> | string;
}

export interface UpdateRoleMappingRequest {
  recid: number;
  role_id: number;
  admin_type?: 'super_admin' | 'admin' | 'operation';
  module_or_screen: Record<string, string> | string;
}

export interface DeleteRoleMappingRequest {
  recid: number;
}

export interface RoleMappingResponse {
  error: boolean;
  message: string;
  data?: RoleMapping;
  roleMappings?: RoleMapping[];
  role_mappings?: RoleMapping[];
  role_detail?: RoleMapping[];
  recId?: number;
}

// ✅ FULL MENU LIST – matches ALL_MENUS from SlideMenu
export const MENU_ITEMS = [
  { id: 'dashboard',                   name: 'Dashboard' },
  { id: 'user_list',                   name: 'User List' },
  { id: 'order_management',            name: 'Order Management' },
  { id: 'bulk_order_management',       name: 'Bulk Order Request' },
  { id: 'return_process_management',   name: 'Return Process Management' },
  { id: 'warranty_management',         name: 'Extended Warranty Products List' },
  { id: 'review',                      name: 'Review' },
  { id: 'guest_order_list',            name: 'Guest User Details' },
  { id: 'guest_user_order',            name: 'Guest Users Order List' },
  { id: 'set_credit',                  name: 'Set Credit for User' },
  { id: 'pickupPending',               name: 'Set to Agent Pick UP' },
  { id: 'deal_of_the_day',             name: 'Deal of the Day' },
  { id: 'reward_management',           name: 'Reward Management' },
  { id: 'reward_settings_management',  name: 'Reward Settings Management' },
  { id: 'admin_rewards',               name: 'User Reward Management' },
  { id: 'flash_sale',                  name: 'Flash Sale' },
  { id: 'delivery_settings',           name: 'Delivery Settings' },
  { id: 'product_management',           name: 'Manage Product List' },
  { id: 'bundle_management',            name: 'Bundle Management' },
  { id: 'category_management',          name: 'Category Management' },
  { id: 'subcategory_management',       name: 'Sub Category Management' },
  { id: 'unit_management',              name: 'Unit Management' },
  { id: 'delivery_agent_management',    name: 'Delivery Agent Management' },
  { id: 'warehouse_management',         name: 'Warehouse Management' },
  { id: 'banner_management',            name: 'Banner Management' },
  { id: 'product_warehouse_management', name: 'Product Warehouse Management' },
  { id: 'delivery_management',          name: 'Delivery Management' },
  { id: 'shipping_management',          name: 'Shipping Management' },
  { id: 'coupon_management',            name: 'Coupon Management' },
  { id: 'admin_coupon_users',           name: 'Coupons Users List' },
  { id: 'admin_user_list',              name: 'Admin User List' },
  { id: 'role_master',                  name: 'Role Master' },
  { id: 'role_mapping',                 name: 'Role Mapping' },
  { id: 'smtp_settings',                name: 'SMTP Settings' },
  { id: 'global_settings',              name: 'Global Settings' },
  { id: 'site_settings',                name: 'Site Settings'},
  { id: 'cash_on_delivery',             name: 'Cash on Delivery'},
  { id: 'about_us',                     name: 'About Us'},
  { id: 'services',                     name: 'Services'},
  { id: 'reports_management',           name: 'Reports Management' },
  { id: 'reports_list',                 name: 'Reports List'},
  { id: 'user_wallet',                  name: 'User Wallet' },
  { id: 'refer_friend',                 name: 'Refer a Friend'},
  { id: 'agent_details',                name: 'Agent Details'},
  { id: 'user_monitor',                 name: 'User Monitor'},
  { id: 'career_management',            name: 'Career Management'},
  { id: 'membership_plan_management',   name: 'Membership Plans'},
  { id: 'support_management',           name: 'Help/Resolution Support'}
];


const parseModuleOrScreen = (mapping: any): any => { 
  const raw = mapping.module_or_screen;
  if (!raw) return mapping;
  if (typeof raw === 'object' && !Array.isArray(raw)) return mapping;
  if (typeof raw === 'string') {
    if (raw.trim() === 'all') return mapping;
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'object' && !Array.isArray(parsed)) {
        return { ...mapping, module_or_screen: parsed };
      }
    } catch {
      // Not valid JSON — leave as-is
    }
  }
  return mapping;
};

/**
 * Ensures every mapping has an 'id' field equal to the API's 'recid' or 'recId'.
 */
const addIdField = (item: any): any => {
  const id = item.recid ?? item.recId ?? item.id;
  return { ...item, id, recid: id };
}; 


/**
  Deduplicate by role_id – keep only the entry with the highest id.
 **/
const deduplicateByRoleId = (mappings: RoleMapping[]): RoleMapping[] => {
  const roleMap = new Map<number, RoleMapping>();

  mappings.forEach((mapping) => {
    const roleId = mapping.role_id;
    if (!roleId) return;

    const existing = roleMap.get(roleId);
    if (!existing) {
      roleMap.set(roleId, mapping);
    } else {
      if (mapping.id > existing.id) {
        roleMap.set(roleId, mapping);
      }
    }
  });

  return Array.from(roleMap.values());
};

export const roleMappingApi = createApi({
  reducerPath: 'roleMappingApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL + '/admin',
    prepareHeaders: async (headers) => {
      let token = localStorage.getItem('admin_token');
      if (!token) token = await adminRefreshToken();
      if (token) headers.set('Authorization', `Bearer ${token}`);
      headers.set('authkey', AUTH_KEY);
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['RoleMapping'],
  endpoints: (builder) => ({

    // List all role mappings — deduplicated by role_id
    listRoleMappings: builder.query<RoleMappingResponse, void>({
      query: () => ({
        url: '/list_role_mappings',
        method: 'POST',
      }),
      providesTags: ['RoleMapping'],
      transformResponse: (response: any) => {
        console.log('Raw list_role_mappings API response:', response);

        const rawMappings = response.role_mappings || [];

        // Step 1: Parse & normalize
        const normalized = rawMappings
          .map(parseModuleOrScreen)
          .map(addIdField);

        // Step 2: Deduplicate — one row per role_id (keep highest id)
        const deduplicated = deduplicateByRoleId(normalized);

        console.log(
          `Mappings: ${rawMappings.length} raw → ${deduplicated.length} after dedup`
        );

        return {
          error: response.error || false,
          message: response.message || '',
          role_mappings: deduplicated,
        };
      },
    }),

    // Create role mapping
    createRoleMapping: builder.mutation<RoleMappingResponse, CreateRoleMappingRequest>({
      query: (data) => ({
        url: '/add_role_detail',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['RoleMapping'],
      transformResponse: (response: any) => {
        console.log('Create role mapping response:', response);
        const data = response.data
          ? addIdField(parseModuleOrScreen(response.data))
          : undefined;
        return { ...response, data };
      },
    }),

    // Update role mapping
    updateRoleMapping: builder.mutation<RoleMappingResponse, UpdateRoleMappingRequest>({
      query: (data) => ({
        url: '/update_role_detail',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['RoleMapping'],
      transformResponse: (response: any) => {
        const data = response.data
          ? addIdField(parseModuleOrScreen(response.data))
          : undefined;
        return { ...response, data };
      },
    }),

    // Delete role mapping
    deleteRoleMapping: builder.mutation<RoleMappingResponse, number>({
      query: (id) => ({
        url: '/delete_role_detail',
        method: 'POST',
        body: { recid: id },
      }),
      invalidatesTags: ['RoleMapping'],
    }),

    // Get role mapping by role ID
    getRoleMappingByRoleId: builder.query<RoleMappingResponse, number>({
      query: (role_id) => ({
        url: '/list_role_detail',
        method: 'POST',
        body: { role_id },
      }),
      transformResponse: (response: any) => {
        console.log('Role detail API response:', response);
        const details = (response.role_detail || [])
          .map(parseModuleOrScreen)
          .map(addIdField);
        return {
          error: response.error || false,
          message: response.message || '',
          role_detail: details,
        };
      },
    }),
  }),
});

export const {
  useListRoleMappingsQuery,
  useCreateRoleMappingMutation,
  useUpdateRoleMappingMutation,
  useDeleteRoleMappingMutation,
  useGetRoleMappingByRoleIdQuery,
} = roleMappingApi;